import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Robot, 
  PauseFill,
  CheckCircleFill,
  List
} from 'react-bootstrap-icons';
import { useAuth } from '../auth/AuthProvider';
import '../../assets/styles/Header.css';

const Header = ({ 
  inFocusedMode = false, 
  moduleDetails = null, 
  difficulty = '', 
  onPauseSession = null, 
  onFinishSession = null,
  getDifficultyLabel = null
}) => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const tabsRef = useRef(null);
  const dropdownRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  
  // Check if current path matches the tab path
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  // Check for horizontal scroll and mobile screen size
  useEffect(() => {
    const checkForScroll = () => {
      if (tabsRef.current) {
        const { scrollWidth, clientWidth } = tabsRef.current;
        setShowScrollIndicator(scrollWidth > clientWidth);
      }
    };
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkForScroll();
    checkMobile();
    
    window.addEventListener('resize', checkForScroll);
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkForScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    }
    
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setShowMoreDropdown(false);
        if (menuButtonRef.current) {
          menuButtonRef.current.focus();
        }
      }
    }
    
    // Only add the event listeners when the dropdown is showing
    if (showMoreDropdown) {
      // Use setTimeout to avoid immediate closure on mobile
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside); // Add touch support
        document.addEventListener('keydown', handleEscapeKey);
      }, 100);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showMoreDropdown]);

  // Define main tabs and dropdown pages
  const mainTabs = [
    { path: '/', label: 'Home' },
    { path: '/UserGuide', label: 'User Guide' },
    { path: '/early-access', label: 'Early Access' },
  ];
  
  const authTabs = [
    { path: '/tutor', label: 'Lessons' },
    { path: '/homework-help', label: 'Homework Help' },
    { path: '/exam-prep', label: 'Exam Prep' },
    { path: '/setup', label: 'Profile' },
  ];
  
  const dropdownPages = [
    { path: '/pitch', label: 'Our Approach' },
    { path: '/parent', label: 'Parent Portal' },
    { path: '/contact', label: 'Contact' },
  ];

  // Check if we're on a page that's in the dropdown
  const isDropdownActive = dropdownPages.some(page => isActive(page.path));
  
  // Toggle dropdown with better mobile positioning and accessibility
  const toggleDropdown = (e) => {
    e.preventDefault(); // Add this to prevent any default behavior
    e.stopPropagation();
    const newState = !showMoreDropdown;
    setShowMoreDropdown(newState);
    
    // Announce state change for screen readers
    if (menuButtonRef.current) {
      menuButtonRef.current.setAttribute('aria-expanded', newState.toString());
    }
    
    // Debug logging for mobile
    if (isMobile) {
      console.log('Mobile dropdown toggle:', newState);
    }
  };
  
  // Handle keyboard navigation for dropdown
  const handleKeyDown = (e) => {
    if (!showMoreDropdown) return;
    
    const dropdown = dropdownRef.current?.querySelector('.dropdown-content');
    if (!dropdown) return;
    
    const focusableElements = dropdown.querySelectorAll('a, button');
    const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
        focusableElements[nextIndex]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
        focusableElements[prevIndex]?.focus();
        break;
      case 'Tab':
        // Allow natural tab behavior but close dropdown if tabbing out
        if (!dropdown.contains(e.relatedTarget)) {
          setShowMoreDropdown(false);
        }
        break;
      default:
        // No action needed for other keys
        break;
    }
  };
  
  // Handle logout
  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate('/login');
    setShowMoreDropdown(false);
  };
  
  // Handle link click
  const handleLinkClick = () => {
    setShowMoreDropdown(false);
  };

  // Function to get difficulty label if not provided as prop
  const getDefaultDifficultyLabel = (diffLevel) => {
    switch(diffLevel) {
      case 'easy': return 'Get the Basics';
      case 'medium': return 'Revision';
      case 'hard': return 'Become an Expert';
      default: return diffLevel.charAt(0).toUpperCase() + diffLevel.slice(1);
    }
  };

  // Use provided function or fallback
  const difficultyLabelFn = getDifficultyLabel || getDefaultDifficultyLabel;

  // Get visible tabs based on screen size and authentication
  const getVisibleTabs = () => {
    if (isMobile) {
      // On mobile, show fewer tabs in the main bar
      const essentialTabs = [
        { path: '/', label: 'Home' },
        ...(isAuthenticated ? [
          { path: '/tutor', label: 'Lessons' },
          { path: '/homework-help', label: 'Help' }
        ] : [
          { path: '/early-access', label: 'Access' }
        ])
      ];
      return essentialTabs;
    }
    
    // Desktop: show main tabs and auth tabs, but move User Guide and Early Access to dropdown when authenticated
    if (isAuthenticated) {
      return [
        { path: '/', label: 'Home' },
        ...authTabs
      ];
    } else {
      return mainTabs;
    }
  };

  // Get dropdown items based on what's not in visible tabs
  const getDropdownItems = () => {
    const visibleTabs = getVisibleTabs();
    const visiblePaths = visibleTabs.map(tab => tab.path);
    
    const hiddenMainTabs = mainTabs.filter(tab => !visiblePaths.includes(tab.path));
    const hiddenAuthTabs = isAuthenticated ? authTabs.filter(tab => !visiblePaths.includes(tab.path)) : [];
    
    // Collect all dropdown items first
    let allDropdownItems = [
      ...hiddenMainTabs,
      ...hiddenAuthTabs,
      ...dropdownPages
    ];
    
    // When authenticated, ensure User Guide and Early Access are in dropdown (add if missing)
    if (isAuthenticated) {
      const existingPaths = allDropdownItems.map(item => item.path);
      
      if (!existingPaths.includes('/UserGuide')) {
        allDropdownItems.unshift({ path: '/UserGuide', label: 'User Guide' });
      }
      if (!existingPaths.includes('/early-access')) {
        allDropdownItems.unshift({ path: '/early-access', label: 'Early Access' });
      }
    }
    
    return allDropdownItems;
  };

  const visibleTabs = getVisibleTabs();
  const dropdownItems = getDropdownItems();

  return (
    <header className={`app-header ${inFocusedMode ? 'focused-mode' : ''}`}>
      <div className="header-container">
        {inFocusedMode ? (
          // Focused Mode Header
          <>
            <div className="focused-header-content">
              {/* Left Section - Brand Only */}
              <div className="focused-header-left">
                <Link 
                  to="/" 
                  className="focused-brand" 
                  aria-label="AI Maths Tutor Home"
                  title="Go to Home"
                >
                  <Robot color="black" size={isMobile ? 18 : 20} />
                  <span className="focused-brand-text">AI Maths Tutor</span>
                </Link>
              </div>
              
              {/* Center Section - Lesson Info */}
              <div className="focused-center">
                {moduleDetails ? (
                  <>
                    <h2 className="focused-lesson-title">
                      Ch {moduleDetails.chapter} - {moduleDetails.section}
                    </h2>
                    <div className="focused-lesson-meta">
                      <span className={`focused-meta-badge difficulty ${difficulty}`}>
                        {difficultyLabelFn(difficulty)}
                      </span>
                    </div>
                  </>
                ) : (
                  <h2 className="focused-lesson-title">
                    AI Maths Tutor Lesson
                  </h2>
                )}
              </div>
              
              {/* Right Section - Action Buttons */}
              <div className="focused-actions">
                <button 
                  className="focused-action-btn secondary"
                  onClick={onPauseSession}
                  aria-label="Pause and exit lesson"
                  title="Pause & Exit"
                >
                  <PauseFill />
                  {!isMobile && <span>Pause & Exit</span>}
                </button>
                <button 
                  className="focused-action-btn primary"
                  onClick={onFinishSession}
                  aria-label="Complete current lesson"
                  title="Complete Lesson"
                >
                  <CheckCircleFill />
                  {!isMobile && <span>Complete</span>}
                </button>
              </div>
            </div>
            
            {/* Mobile lesson meta info */}
            {moduleDetails && isMobile && (
              <div className="focused-mobile-meta">
                <span className="focused-meta-badge chapter">
                  Ch {moduleDetails.chapter} - {moduleDetails.section}
                </span>
                <span className={`focused-meta-badge difficulty ${difficulty}`}>
                  {difficultyLabelFn(difficulty)}
                </span>
              </div>
            )}
          </>
        ) : (
          // Regular Header with three sections
          <>
            {/* Left Section - Title */}
            <div className="header-left">
              <Link 
                to="/" 
                className="brand-title" 
                aria-label="AI Maths Tutor Home"
                title="Go to Home"
              >
                <Robot color="black" size={isMobile ? 18 : 20} />
                <span className="brand-text">AI Maths Tutor</span>
              </Link>
            </div>
            
            {/* Center Section - Navigation */}
            <nav role="navigation" aria-label="Main navigation" className="header-center">
              <div className="notebook-tabs" ref={tabsRef}>
                {visibleTabs.map(tab => (
                  <Link 
                    key={tab.path} 
                    to={tab.path} 
                    className={`notebook-tab ${isActive(tab.path) ? 'active' : ''}`}
                    title={tab.label}
                  >
                    {tab.label}
                  </Link>
                ))}
                
                {/* More dropdown - only show if there are items */}
                {dropdownItems.length > 0 && (
                  <div className="more-tabs-dropdown" ref={dropdownRef}>
                    <button 
                      ref={menuButtonRef}
                      className={`notebook-tab menu-tab ${isDropdownActive || showMoreDropdown ? 'active' : ''}`}
                      onClick={toggleDropdown}
                      onKeyDown={handleKeyDown}
                      aria-expanded={showMoreDropdown}
                      aria-haspopup="true"
                      aria-label={`Menu ${showMoreDropdown ? '(expanded)' : '(collapsed)'}`}
                      title="Open menu"
                    >
                      {isMobile ? (
                        <List size={18} />
                      ) : (
                        <>
                          <span className="menu-label">Menu</span>
                          <span className="menu-icon-down">▾</span>
                        </>
                      )}
                    </button>
                    
                    {showMoreDropdown && (
                      <div 
                        className="dropdown-content show"
                        role="menu"
                        aria-labelledby="menu-button"
                        onKeyDown={handleKeyDown}
                      >
                        {dropdownItems.map(item => (
                          <Link 
                            key={item.path} 
                            to={item.path}
                            onClick={handleLinkClick}
                            role="menuitem"
                            tabIndex={0}
                          >
                            {item.label}
                          </Link>
                        ))}
                        
                        <div className="dropdown-divider" role="separator"></div>
                        
                        {isAuthenticated ? (
                          <button 
                            onClick={handleLogout}
                            role="menuitem"
                            tabIndex={0}
                          >
                            Log Out
                          </button>
                        ) : (
                          <>
                            <Link 
                              to="/login" 
                              onClick={handleLinkClick}
                              role="menuitem"
                              tabIndex={0}
                            >
                              Log In
                            </Link>
                            <Link 
                              to="/signup" 
                              onClick={handleLinkClick}
                              role="menuitem"
                              tabIndex={0}
                            >
                              Sign Up
                            </Link>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {showScrollIndicator && <div className="scroll-indicator" />}
            </nav>
            
            {/* Right Section - Authentication Buttons */}
            <div className="header-right">
              {isAuthenticated ? (
                <button 
                  className="auth-btn logout-btn"
                  onClick={handleLogout}
                  aria-label="Log out"
                  title="Log Out"
                >
                  Log Out
                </button>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="auth-btn login-btn"
                    aria-label="Log in"
                    title="Log In"
                  >
                    Log In
                  </Link>
                  <Link 
                    to="/signup" 
                    className="auth-btn signup-btn"
                    aria-label="Sign up"
                    title="Sign Up"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;